"use client";

import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import type { Folder, FolderWithChildren, Thread } from "@/types/chat";
type UseFoldersResult = {
  folders: Folder[];
  folderTree: FolderWithChildren[];
  defaultFolderId: string | null;
  loadingFolders: boolean;
  foldersError: string | null;
  createFolder: (name: string, parentId?: string | null) => Promise<Folder | null>;
  updateFolder: (id: string, updates: Partial<Pick<Folder, 'name' | 'color' | 'icon' | 'is_collapsed'>>) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  moveFolder: (folderId: string, newParentId: string | null) => Promise<void>;
  moveThread: (threadId: string, folderId: string | null) => Promise<void>;
  bulkMoveThreads: (threadIds: string[], folderId: string | null) => Promise<void>;
  reorderFolders: (folderId: string, newPosition: number, parentId: string | null) => Promise<void>;
  reorderThreads: (threadId: string, newPosition: number, folderId: string | null) => Promise<void>;
  toggleFolderCollapse: (folderId: string) => Promise<void>;
  getOrCreateDefaultFolder: () => Promise<string | null>;
  refreshFolders: () => Promise<void>;
};

/**
 * Build a tree structure from flat folder list
 */
function buildFolderTree(folders: Folder[], threads: Thread[]): FolderWithChildren[] {
  // Create a map for quick lookup
  const folderMap = new Map<string, FolderWithChildren>();

  // Initialize all folders with empty children and threads
  folders.forEach(folder => {
    folderMap.set(folder.id, {
      ...folder,
      children: [],
      threads: []
    });
  });

  // Assign threads to their folders
  threads.forEach(thread => {
    if (thread.folder_id && folderMap.has(thread.folder_id)) {
      folderMap.get(thread.folder_id)!.threads.push(thread);
    }
  });

  // Sort threads within each folder by position
  folderMap.forEach(folder => {
    folder.threads.sort((a, b) => a.position - b.position);
  });

  // Build tree structure
  const rootFolders: FolderWithChildren[] = [];
  folders.forEach(folder => {
    const folderWithChildren = folderMap.get(folder.id)!;
    if (folder.parent_id && folderMap.has(folder.parent_id)) {
      // Add to parent's children
      folderMap.get(folder.parent_id)!.children.push(folderWithChildren);
    } else {
      // Add to root
      rootFolders.push(folderWithChildren);
    }
  });

  // Sort children within each folder by position
  const sortChildren = (folder: FolderWithChildren) => {
    folder.children.sort((a, b) => a.position - b.position);
    folder.children.forEach(sortChildren);
  };
  rootFolders.sort((a, b) => a.position - b.position);
  rootFolders.forEach(sortChildren);
  return rootFolders;
}
type UseFoldersOptions = {
  onThreadMoved?: () => void; // Callback to refresh threads after move
};
export function useFolders(userId: string | null | undefined, threads: Thread[], options: UseFoldersOptions = {}): UseFoldersResult {
  const {
    onThreadMoved
  } = options;
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [foldersError, setFoldersError] = useState<string | null>(null);

  // Build tree structure whenever folders or threads change
  const folderTree = buildFolderTree(folders, threads);
  const refreshFolders = useCallback(async () => {
    if (!userId) return;
    setLoadingFolders(true);
    setFoldersError(null);
    try {
      const {
        data,
        error
      } = await supabase.from("folders").select("*").eq("user_id", userId).order("position", {
        ascending: true
      });
      if (error) throw error;
      setFolders(data ?? []);
    } catch (err: any) {
      setFoldersError(err.message ?? "Failed to load directories");
    } finally {
      setLoadingFolders(false);
    }
  }, [userId]);
  useEffect(() => {
    if (!userId) {
      setFolders([]);
      return;
    }
    void refreshFolders();
  }, [userId, refreshFolders]);
  const createFolder = async (name: string, parentId: string | null = null): Promise<Folder | null> => {
    if (!userId) return null;
    try {
      // Get the highest position among siblings to add at the end
      const siblings = folders.filter(f => f.parent_id === parentId);
      const maxPosition = siblings.length > 0 ? Math.max(...siblings.map(f_0 => f_0.position)) + 1 : 0;
      const {
        data: data_0,
        error: error_0
      } = await supabase.from("folders").insert({
        user_id: userId,
        parent_id: parentId,
        name,
        position: maxPosition
      }).select().single();
      if (error_0) throw error_0;
      if (!data_0) return null;
      setFolders(prev => [...prev, data_0]);
      return data_0;
    } catch (err_0: any) {
      console.error("Error creating folder:", err_0);
      setFoldersError(err_0.message ?? "Failed to create directory");
      return null;
    }
  };
  const updateFolder = async (id: string, updates: Partial<Pick<Folder, 'name' | 'color' | 'icon' | 'is_collapsed'>>): Promise<void> => {
    if (!userId) return;
    try {
      const {
        error: error_1
      } = await supabase.from("folders").update(updates).eq("id", id).eq("user_id", userId);
      if (error_1) throw error_1;
      setFolders(prev_0 => prev_0.map(f_1 => f_1.id === id ? {
        ...f_1,
        ...updates
      } : f_1));
    } catch (err_1: any) {
      console.error("Error updating folder:", err_1);
      setFoldersError(err_1.message ?? "Failed to update directory");
    }
  };
  const deleteFolder = async (id_0: string): Promise<void> => {
    if (!userId) return;

    // Prevent deletion of default folder
    const folder = folders.find(f_2 => f_2.id === id_0);
    if (folder?.is_default) {
      setFoldersError("Cannot delete the default directory");
      return;
    }
    try {
      const {
        error: error_2
      } = await supabase.from("folders").delete().eq("id", id_0).eq("user_id", userId);
      if (error_2) throw error_2;

      // Remove folder and all its descendants from local state
      const getDescendantIds = (folderId: string): string[] => {
        const descendants: string[] = [];
        const children = folders.filter(f_3 => f_3.parent_id === folderId);
        children.forEach(child => {
          descendants.push(child.id);
          descendants.push(...getDescendantIds(child.id));
        });
        return descendants;
      };
      const idsToRemove = new Set([id_0, ...getDescendantIds(id_0)]);
      setFolders(prev_1 => prev_1.filter(f_4 => !idsToRemove.has(f_4.id)));
    } catch (err_2: any) {
      console.error("Error deleting folder:", err_2);
      setFoldersError(err_2.message ?? "Failed to delete directory");
    }
  };
  const moveFolder = async (folderId_0: string, newParentId: string | null): Promise<void> => {
    if (!userId) return;
    try {
      // Get position for new parent's children
      const newSiblings = folders.filter(f_5 => f_5.parent_id === newParentId && f_5.id !== folderId_0);
      const maxPosition_0 = newSiblings.length > 0 ? Math.max(...newSiblings.map(f_6 => f_6.position)) + 1 : 0;
      const {
        error: error_3
      } = await supabase.from("folders").update({
        parent_id: newParentId,
        position: maxPosition_0
      }).eq("id", folderId_0).eq("user_id", userId);
      if (error_3) throw error_3;
      setFolders(prev_2 => prev_2.map(f_7 => f_7.id === folderId_0 ? {
        ...f_7,
        parent_id: newParentId,
        position: maxPosition_0
      } : f_7));
    } catch (err_3: any) {
      console.error("Error moving folder:", err_3);
      setFoldersError(err_3.message ?? "Failed to move directory");
    }
  };
  const moveThread = async (threadId: string, folderId_1: string | null): Promise<void> => {
    if (!userId) return;
    try {
      // Get position for the folder's threads
      const folderThreads = threads.filter(t => t.folder_id === folderId_1 && t.id !== threadId);
      const maxPosition_1 = folderThreads.length > 0 ? Math.max(...folderThreads.map(t_0 => t_0.position)) + 1 : 0;
      const {
        error: error_4
      } = await supabase.from("threads").update({
        folder_id: folderId_1,
        position: maxPosition_1
      }).eq("id", threadId).eq("user_id", userId);
      if (error_4) {
        console.error("Supabase error moving thread:", error_4.message, error_4.details, error_4.hint);
        throw new Error(error_4.message || "Database error");
      }

      // Refresh threads to update the UI
      if (onThreadMoved) {
        onThreadMoved();
      }
    } catch (err_4: any) {
      console.error("Error moving thread:", err_4?.message || err_4);
      setFoldersError(err_4?.message ?? "Failed to move thread");
    }
  };
  const bulkMoveThreads = async (threadIds: string[], folderId_2: string | null): Promise<void> => {
    if (!userId || threadIds.length === 0) return;
    try {
      // Get current max position in target folder
      const folderThreads_0 = threads.filter(t_1 => t_1.folder_id === folderId_2 && !threadIds.includes(t_1.id));
      let nextPosition = folderThreads_0.length > 0 ? Math.max(...folderThreads_0.map(t_2 => t_2.position)) + 1 : 0;

      // Update all threads in parallel
      const updatePromises = threadIds.map((threadId_0, index) => supabase.from("threads").update({
        folder_id: folderId_2,
        position: nextPosition + index
      }).eq("id", threadId_0).eq("user_id", userId));
      const results = await Promise.all(updatePromises);

      // Check for errors
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error("Supabase errors moving threads:", errors.map(e => e.error?.message));
        throw new Error(`Failed to move ${errors.length} thread(s)`);
      }

      // Refresh threads to update the UI
      if (onThreadMoved) {
        onThreadMoved();
      }
    } catch (err_5: any) {
      console.error("Error bulk moving threads:", err_5?.message || err_5);
      setFoldersError(err_5?.message ?? "Failed to move threads");
    }
  };
  const reorderFolders = async (folderId_3: string, newPosition: number, parentId_0: string | null): Promise<void> => {
    if (!userId) return;
    try {
      // Get all siblings
      const siblings_0 = folders.filter(f_8 => f_8.parent_id === parentId_0 && f_8.id !== folderId_3).sort((a, b) => a.position - b.position);

      // Insert at new position
      const folder_0 = folders.find(f_9 => f_9.id === folderId_3);
      if (!folder_0) return;
      const reorderedSiblings = [...siblings_0];
      reorderedSiblings.splice(newPosition, 0, folder_0);

      // Update positions
      const updates_0 = reorderedSiblings.map((f_10, idx) => ({
        id: f_10.id,
        position: idx
      }));

      // Batch update (in parallel)
      await Promise.all(updates_0.map(u => supabase.from("folders").update({
        position: u.position
      }).eq("id", u.id).eq("user_id", userId)));

      // Update local state
      setFolders(prev_3 => prev_3.map(f_11 => {
        const update = updates_0.find(u_0 => u_0.id === f_11.id);
        return update ? {
          ...f_11,
          position: update.position
        } : f_11;
      }));
    } catch (err_6: any) {
      console.error("Error reordering folders:", err_6);
      setFoldersError(err_6.message ?? "Failed to reorder directories");
    }
  };
  const reorderThreads = async (threadId_1: string, newPosition_0: number, folderId_4: string | null): Promise<void> => {
    if (!userId) return;
    try {
      // Get all threads in the folder
      const folderThreads_1 = threads.filter(t_3 => t_3.folder_id === folderId_4 && t_3.id !== threadId_1).sort((a_0, b_0) => a_0.position - b_0.position);

      // Find the thread being moved
      const thread = threads.find(t_4 => t_4.id === threadId_1);
      if (!thread) return;

      // Insert at new position
      const reorderedThreads = [...folderThreads_1];
      reorderedThreads.splice(newPosition_0, 0, thread);

      // Update positions
      const updates_1 = reorderedThreads.map((t_5, idx_0) => ({
        id: t_5.id,
        position: idx_0
      }));

      // Batch update
      await Promise.all(updates_1.map(u_1 => supabase.from("threads").update({
        position: u_1.position
      }).eq("id", u_1.id).eq("user_id", userId)));

      // Note: Thread state is managed by useThreads
    } catch (err_7: any) {
      console.error("Error reordering threads:", err_7);
      setFoldersError(err_7.message ?? "Failed to reorder threads");
    }
  };
  const toggleFolderCollapse = async (folderId_5: string): Promise<void> => {
    const folder_1 = folders.find(f_12 => f_12.id === folderId_5);
    if (!folder_1) return;
    await updateFolder(folderId_5, {
      is_collapsed: !folder_1.is_collapsed
    });
  };

  // Get or create the default folder for the user
  const getOrCreateDefaultFolder = async (): Promise<string | null> => {
    if (!userId) return null;
    try {
      // First check if we already have a default folder loaded
      const existingDefault = folders.find(f_13 => f_13.is_default);
      if (existingDefault) return existingDefault.id;

      // Check in database
      const {
        data: existing,
        error: fetchError
      } = await supabase.from("folders").select("id").eq("user_id", userId).eq("is_default", true).single();
      if (existing) return existing.id;

      // Create default folder if it doesn't exist
      const {
        data: newFolder,
        error: createError
      } = await supabase.from("folders").insert({
        user_id: userId,
        name: "General",
        is_default: true,
        position: 0
      }).select().single();
      if (createError) {
        console.error("Error creating default folder:", createError);
        return null;
      }

      // Add to local state
      if (newFolder) {
        setFolders(prev_4 => [newFolder, ...prev_4]);
        return newFolder.id;
      }
      return null;
    } catch (err_8: any) {
      console.error("Error getting/creating default folder:", err_8);
      return null;
    }
  };

  // Get the default folder ID from the loaded folders
  const defaultFolderId = folders.find(f_14 => f_14.is_default)?.id ?? null;
  return {
    folders,
    folderTree,
    defaultFolderId,
    loadingFolders,
    foldersError,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    moveThread,
    bulkMoveThreads,
    reorderFolders,
    reorderThreads,
    toggleFolderCollapse,
    getOrCreateDefaultFolder,
    refreshFolders
  };
}